import { useEffect, useState } from 'react'
import { supabase } from '../../../lib/supabase'
import AdminLayout from '../components/AdminLayout'
import SubscriptionsTable from './components/SubscriptionsTable'
import PaymentForm from './components/PaymentForm'
import LoadingSpinner from '../../../components/LoadingSpinner'
import { sendBulkReminders, sendSeasonEndingEmails, sendSeasonEndedEmails } from '../../../lib/emailService'

const CLUB_ID = 'a0000000-0000-0000-0000-000000000001'

export default function AdminSubscriptions() {
  const [members, setMembers] = useState([])
  const [settings, setSettings] = useState({})
  const [loading, setLoading] = useState(true)
  const [selectedMember, setSelectedMember] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [filter, setFilter] = useState('all')
  const [sendingReminders, setSendingReminders] = useState(false)
  const [reminderResult, setReminderResult] = useState(null)

  const fetchData = async () => {
    const { data: settingsData } = await supabase.from('settings').select('*')
    if (settingsData) {
      const mapped = {}
      settingsData.forEach(s => mapped[s.key] = s.value)
      setSettings(mapped)
    }
    const { data: membersData } = await supabase
      .from('supporters').select('*, chapters(name), subscriptions(*)')
      .eq('club_id', CLUB_ID).eq('role', 'supporter').order('joined_at', { ascending: false })
    if (membersData) setMembers(membersData)
    setLoading(false)
  }

  useEffect(() => { fetchData() }, [])

  const getSubscriptionStatus = (member) => {
    if (!member.subscriptions || member.subscriptions.length === 0) return 'unpaid'
    const latest = member.subscriptions.sort((a, b) => new Date(b.payment_date) - new Date(a.payment_date))[0]
    if (latest.status === 'inactive') return 'expired'
    if (new Date(latest.valid_until) < new Date()) return 'expired'
    return 'paid'
  }

  const handleSendReminders = async (type) => {
    setSendingReminders(true)
    setReminderResult(null)
    try {
      let result
      if (type === 'unpaid') result = await sendBulkReminders({ club_id: CLUB_ID })
      else if (type === 'ending') result = await sendSeasonEndingEmails({ club_id: CLUB_ID })
      else result = await sendSeasonEndedEmails({ club_id: CLUB_ID })
      setReminderResult({ success: result.success, message: result.message, sent: result.sent })
    } catch {
      setReminderResult({ success: false, message: 'Failed to send reminders' })
    }
    setSendingReminders(false)
  }

  const filteredMembers = members.filter(m => filter === 'all' || getSubscriptionStatus(m) === filter)

  const stats = {
    total: members.length,
    paid: members.filter(m => getSubscriptionStatus(m) === 'paid').length,
    unpaid: members.filter(m => getSubscriptionStatus(m) === 'unpaid').length,
    expired: members.filter(m => getSubscriptionStatus(m) === 'expired').length,
  }

  const daysUntilSeasonEnd = settings.season_end_date
    ? Math.max(0, Math.ceil((new Date(settings.season_end_date) - new Date()) / (1000 * 60 * 60 * 24)))
    : null

  const seasonEnded = settings.season_end_date ? new Date() > new Date(settings.season_end_date) : false

  if (loading) return <LoadingSpinner fullScreen />

  return (
    <AdminLayout>
      <div className="mb-6 lg:mb-8 flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div>
          <h1 className="text-2xl lg:text-4xl font-black uppercase tracking-tight mb-2 text-gray-900 dark:text-white">Subscriptions</h1>
          <p className="text-gray-500 text-sm">{settings.season_name || 'Current Season'} · Track and manage member payments</p>
        </div>
        <div className="flex flex-row sm:flex-col gap-2 sm:items-end flex-wrap">
          <button onClick={() => handleSendReminders('unpaid')} disabled={sendingReminders || stats.unpaid === 0} className="bg-yellow-600 hover:bg-yellow-700 disabled:bg-yellow-900 disabled:text-yellow-700 text-white px-4 sm:px-5 py-2 font-bold uppercase tracking-widest text-xs transition-colors whitespace-nowrap">
            {sendingReminders ? 'Sending...' : `Remind Unpaid (${stats.unpaid})`}
          </button>
          {daysUntilSeasonEnd !== null && daysUntilSeasonEnd <= 14 && !seasonEnded && (
            <button onClick={() => handleSendReminders('ending')} disabled={sendingReminders} className="bg-orange-600 hover:bg-orange-700 disabled:bg-orange-900 text-white px-4 sm:px-5 py-2 font-bold uppercase tracking-widest text-xs transition-colors whitespace-nowrap">
              {sendingReminders ? 'Sending...' : 'Notify Season Ending'}
            </button>
          )}
          {seasonEnded && (
            <button onClick={() => handleSendReminders('ended')} disabled={sendingReminders} className="bg-red-600 hover:bg-red-700 disabled:bg-red-900 text-white px-4 sm:px-5 py-2 font-bold uppercase tracking-widest text-xs transition-colors whitespace-nowrap">
              {sendingReminders ? 'Sending...' : 'Notify Season Ended'}
            </button>
          )}
        </div>
      </div>

      {reminderResult && (
        <div className={`px-4 py-3 text-sm mb-6 border ${reminderResult.success ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800/50 text-green-600 dark:text-green-400' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400'}`}>
          {reminderResult.message}{reminderResult.sent > 0 && ` — ${reminderResult.sent} emails sent`}
        </div>
      )}

      {settings.season_end_date && (
        <div className={`px-4 py-3 text-sm mb-6 border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 ${seasonEnded ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400' : daysUntilSeasonEnd <= 14 ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800/50 text-yellow-600 dark:text-yellow-400' : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800/50 text-green-600 dark:text-green-400'}`}>
          <span className="font-bold uppercase tracking-widest text-xs">{settings.season_name || 'Current Season'}</span>
          <span className="text-xs">{seasonEnded ? 'Season has ended' : `${daysUntilSeasonEnd} days until season ends · ${new Date(settings.season_end_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`}</span>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-6 lg:mb-8">
        {[
          { label: 'Total Members', value: stats.total, color: 'text-gray-900 dark:text-white' },
          { label: 'Paid', value: stats.paid, color: 'text-green-600 dark:text-green-400' },
          { label: 'Unpaid', value: stats.unpaid, color: 'text-red-500 dark:text-red-400' },
          { label: 'Expired', value: stats.expired, color: 'text-yellow-600 dark:text-yellow-400' },
        ].map((stat) => (
          <div key={stat.label} className="bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-4 lg:p-6">
            <div className={`text-3xl lg:text-4xl font-black mb-2 ${stat.color}`}>{stat.value}</div>
            <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold">{stat.label}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {['all', 'paid', 'unpaid', 'expired'].map((f) => (
          <button key={f} onClick={() => setFilter(f)} className={`px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors ${filter === f ? 'bg-green-600 text-white' : 'border border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'}`}>{f}</button>
        ))}
      </div>

      <SubscriptionsTable members={filteredMembers} settings={settings} getSubscriptionStatus={getSubscriptionStatus} onRecordPayment={(member) => { setSelectedMember(member); setShowForm(true) }} onRefresh={fetchData} />

      {showForm && selectedMember && (
        <PaymentForm member={selectedMember} settings={settings} onClose={() => { setShowForm(false); setSelectedMember(null) }} onRefresh={fetchData} />
      )}
    </AdminLayout>
  )
}